import React, {ReactNode, useEffect, useMemo} from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {fetchGridConfig, saveGridConfig} from 'api/gridsConfig';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {ROUTE} from 'core/routes';
import {lastCreatedDocumentId$, lastCreatedInvocieId$} from 'models/documents/create';
import {
    filtersChanged,
    filtersReset,
    pageNumberChanged,
    refetchWithSortByDescDate,
    sortingsChanged,
    stateReset,
    tableState$,
    updOnlyChanged,
    Gate,
} from 'models/documents/documents/grid/table';
import {DocumentsGridFormattedRow, DocumentsGridRawRow} from 'types';

interface Props {
    configFunc: (t: TFunction, showType: boolean) => Array<any>;
    dataMapper: (
        data: DocumentsGridRawRow[] | undefined,
        t: TFunction,
        lastCreatedDocumentId?: string | null,
        lastCreatedInvoiceId?: string | null,
        updOnly?: boolean,
    ) => DocumentsGridFormattedRow[];
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
    headerControls?: ReactNode;
    headerControlsAlignItems?: string;
}

export const DocumentsGridTable = ({
    configFunc,
    dataMapper,
    headerControls,
    exportConfig,
    headerControlsAlignItems,
}: Props) => {
    useGate(Gate);
    const INVOICES = [
        ROUTE.DOCUMENTS_INVOICES,
        ROUTE.DOCUMENTS_INVOICES_INCOMING,
        ROUTE.DOCUMENTS_INVOICES_OUTGOING,
    ];
    const TRANSFER_DOCUMENTS = [
        ROUTE.DOCUMENTS_TRANSFER,
        ROUTE.DOCUMENTS_TRANSFER_INCOMING,
        ROUTE.DOCUMENTS_TRANSFER_OUTGOING,
    ];
    updOnlyChanged(
        INVOICES.some((i) => location.pathname === i) ||
            TRANSFER_DOCUMENTS.some((i) => location.pathname === i) ||
            location.pathname === ROUTE.DOCUMENTS_MOVING,
    );
    const {t} = useTranslation();
    const {data, pageNumber, isLoading, filters, sortings, updOnly} = useStore(tableState$);
    const isOutgoingInvoice = location.pathname === ROUTE.DOCUMENTS_INVOICES_OUTGOING;

    const lastCreatedDocumentId = useStore(lastCreatedDocumentId$);
    const lastCreatedInvocieId = useStore(lastCreatedInvocieId$);

    const dataWithMocked = useMemo(() => {
        if (!data?.content) return undefined;
        const content = [...data.content];

        if (!isOutgoingInvoice) {
            return content;
        }

        return content;
    }, [data]);

    const INVOICE_PAGES: Record<string, string> = {
        [ROUTE.DOCUMENTS_INVOICES]: 'invoicesGrid',
        [ROUTE.DOCUMENTS_INVOICES_INCOMING]: 'invoicesGrid',
        [ROUTE.DOCUMENTS_INVOICES_OUTGOING]: 'invoicesGrid',
        [ROUTE.DOCUMENTS_MOVING]: 'movingGrid',
        [ROUTE.DOCUMENTS_TRANSFER]: 'transferDocumentsGrid',
        [ROUTE.DOCUMENTS_TRANSFER_INCOMING]: 'transferDocumentsGrid',
        [ROUTE.DOCUMENTS_TRANSFER_OUTGOING]: 'transferDocumentsGrid',
    };

    useEffect(() => {
        stateReset();
        refetchWithSortByDescDate();
    }, []);

    const columns = useMemo(
        () =>
            mapTableColumns(
                configFunc(t, !updOnly),
                filters,
                filtersChanged,
                filtersReset,
                sortings,
                sortingsChanged,
            ),
        [t, filters, sortings],
    );

    const mappedData = React.useMemo(
        () => dataMapper(dataWithMocked, t, lastCreatedDocumentId, lastCreatedInvocieId, updOnly),
        [dataMapper, data, t, lastCreatedDocumentId, lastCreatedInvocieId],
    );

    return (
        <>
            <Table
                headerControlsAlignItems={headerControlsAlignItems}
                tableTabsSettings={
                    INVOICES.some((i) => i === location.pathname) ||
                    TRANSFER_DOCUMENTS.some((i) => i === location.pathname)
                        ? {
                              baseUrl: INVOICES.includes(location.pathname as ROUTE)
                                  ? '/invoices'
                                  : '/documents/transfer',
                              links: [
                                  {label: t('pages.documents.invoices.incoming'), path: 'incoming'},
                                  {label: t('pages.documents.invoices.outgoing'), path: 'outgoing'},
                              ],
                          }
                        : undefined
                }
                columns={columns}
                data={mappedData}
                hasNext={data?.hasNext}
                pageIndex={pageNumber}
                onPageChange={pageNumberChanged}
                isLoading={isLoading}
                userConfig={{
                    gridName: INVOICE_PAGES[location.pathname] ?? 'documentsGrid',
                    saveConfig: saveGridConfig,
                    getConfig: fetchGridConfig,
                }}
                headerControls={headerControls}
                exportConfig={exportConfig}
                cellContainerSettings={
                    INVOICE_PAGES[location.pathname] ? 'width: 100%; height: 60px;' : undefined
                }
            />
        </>
    );
};
